/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * An MCP endpoint: a url-friendly slug identifier that addresses an MCP server or a meta MCP server. Exactly one of mcp_server_id and meta_mcp_server_id is set.
 */
export type McpEndpoint = {
  /**
   * When the MCP endpoint was created
   */
  createdAt: Date;
  /**
   * The ID of the custom domain this endpoint slug is registered under. Null for platform-domain endpoints.
   */
  customDomainId?: string | undefined;
  /**
   * The ID of the MCP endpoint
   */
  id: string;
  /**
   * Whether this endpoint is mapped to its custom-domain root
   */
  isDomainRoot: boolean;
  /**
   * The ID of the MCP server this endpoint addresses. Null for meta-MCP-backed endpoints.
   */
  mcpServerId?: string | undefined;
  /**
   * The ID of the meta MCP server this endpoint addresses. Null for MCP-server-backed endpoints.
   */
  metaMcpServerId?: string | undefined;
  /**
   * The project ID this MCP endpoint belongs to
   */
  projectId: string;
  /**
   * A url-friendly label (up to 128 characters) that addresses an MCP server through a slug-based URL. Platform-domain slugs (no custom domain) must be prefixed with the organization slug.
   */
  slug: string;
  /**
   * When the MCP endpoint was last updated
   */
  updatedAt: Date;
};

/** @internal */
export const McpEndpoint$inboundSchema: z.ZodMiniType<McpEndpoint, unknown> = z
  .pipe(
    z.object({
      created_at: z.pipe(
        z.iso.datetime({ offset: true }),
        z.transform(v => new Date(v)),
      ),
      custom_domain_id: z.optional(z.string()),
      id: z.string(),
      is_domain_root: z.boolean(),
      mcp_server_id: z.optional(z.string()),
      meta_mcp_server_id: z.optional(z.string()),
      project_id: z.string(),
      slug: z.string(),
      updated_at: z.pipe(
        z.iso.datetime({ offset: true }),
        z.transform(v => new Date(v)),
      ),
    }),
    z.transform((v) => {
      return remap$(v, {
        "created_at": "createdAt",
        "custom_domain_id": "customDomainId",
        "is_domain_root": "isDomainRoot",
        "mcp_server_id": "mcpServerId",
        "meta_mcp_server_id": "metaMcpServerId",
        "project_id": "projectId",
        "updated_at": "updatedAt",
      });
    }),
  );

export function mcpEndpointFromJSON(
  jsonString: string,
): SafeParseResult<McpEndpoint, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => McpEndpoint$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'McpEndpoint' from JSON`,
  );
}
